/**
 * Admin Economy Charts Component
 * Displays comprehensive economy charts for administrators
 */

export function initEconomyCharts() {
  return {
    // State
    days: 7,
    loading: false,
    error: null,
    chartsReady: false,
    factorsChart: null,
    transactionsChart: null,
    
    // Data
    dailyEconomy: [],
    transactions: [],
    todayData: null,

    async init() {
      console.log('Initializing Economy Charts...');
      await this.loadData();
      await this.initCharts();
    },

    async loadData() {
      this.loading = true;
      this.error = null;
      
      try {
        // Load metrics series data
        const response = await fetch(`/api/admin/metrics/series?days=${this.days}&type=economy`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`API Error: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success && data.data.series) {
          this.dailyEconomy = data.data.series.dailyEconomy || [];
          this.transactions = data.data.series.transactions || [];
        }

        // Load today's economy data for highlighting
        try {
          const todayResponse = await fetch('/api/economy/today', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (todayResponse.ok) {
            const todayData = await todayResponse.json();
            this.todayData = todayData;
          }
        } catch (err) {
          console.warn('Could not load today data:', err);
        }

      } catch (error) {
        console.error('Error loading economy data:', error);
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },

    async initCharts() {
      if (this.dailyEconomy.length === 0) {
        console.warn('No data available for charts');
        return;
      }

      // Use requestIdleCallback to avoid blocking UI
      const initCallback = async () => {
        try {
          // Dynamic import of Chart.js
          const { Chart } = await import('chart.js/auto');
          
          await this.createFactorsChart(Chart);
          await this.createTransactionsChart(Chart);
          
          this.chartsReady = true;
          console.log('Economy charts ready');
        } catch (error) {
          console.error('Error initializing charts:', error);
          this.error = 'Failed to initialize charts';
        }
      };

      if (window.requestIdleCallback) {
        window.requestIdleCallback(initCallback);
      } else {
        setTimeout(initCallback, 0);
      }
    },

    async createFactorsChart(Chart) {
      const canvas = document.querySelector('[data-testid="admin-econ-chart-factors"]');
      if (!canvas) {
        console.warn('Factors chart canvas not found');
        return;
      }

      // Destroy existing chart
      if (this.factorsChart) {
        this.factorsChart.destroy();
      }

      const labels = this.dailyEconomy.map(day => {
        const date = new Date(day.dateKey);
        return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
      });

      const marketData = this.dailyEconomy.map(day => day.marketFactor);
      const travelData = this.dailyEconomy.map(day => day.travelFeeFactor);

      this.factorsChart = new Chart(canvas, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Factor de Mercado',
              data: marketData,
              borderColor: 'rgb(59, 130, 246)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              tension: 0.4
            },
            {
              label: 'Factor de Viaje',
              data: travelData,
              borderColor: 'rgb(16, 185, 129)',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              tension: 0.4
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: false,
              title: {
                display: true,
                text: 'Factor'
              }
            }
          },
          plugins: {
            title: {
              display: true,
              text: 'Factores Económicos Diarios'
            },
            legend: {
              display: true
            }
          }
        }
      });
    },

    async createTransactionsChart(Chart) {
      const canvas = document.querySelector('[data-testid="admin-econ-chart-transactions"]');
      if (!canvas) {
        console.warn('Transactions chart canvas not found');
        return;
      }

      // Destroy existing chart
      if (this.transactionsChart) {
        this.transactionsChart.destroy();
      }

      // Group transactions by type and date
      const transactionTypes = [...new Set(this.transactions.map(t => t.type))];
      const labels = this.dailyEconomy.map(day => {
        const date = new Date(day.dateKey);
        return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
      });

      const datasets = transactionTypes.map((type, index) => {
        const data = this.dailyEconomy.map(day => {
          const dayTransactions = this.transactions.filter(t => 
            t.dateKey === day.dateKey && t.type === type
          );
          return dayTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);
        });

        // Color palette for different transaction types
        const colors = [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(14, 165, 233, 0.8)',
          'rgba(34, 197, 94, 0.8)'
        ];

        return {
          label: type.replace(/_/g, ' '),
          data,
          backgroundColor: colors[index % colors.length],
          borderColor: colors[index % colors.length],
          borderWidth: 1
        };
      });

      this.transactionsChart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels,
          datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: true
            },
            y: {
              stacked: true,
              beginAtZero: true,
              title: {
                display: true,
                text: 'Volumen (monedas)'
              }
            }
          },
          plugins: {
            title: {
              display: true,
              text: 'Volumen de Transacciones por Tipo'
            },
            legend: {
              display: true,
              position: 'top'
            }
          }
        }
      });
    },

    async changeDays(newDays) {
      if (this.days === newDays || this.loading) return;
      
      this.days = newDays;
      await this.loadData();
      
      if (this.chartsReady) {
        await this.initCharts();
      }
    },

    destroy() {
      if (this.factorsChart) {
        this.factorsChart.destroy();
        this.factorsChart = null;
      }
      if (this.transactionsChart) {
        this.transactionsChart.destroy();
        this.transactionsChart = null;
      }
      this.chartsReady = false;
    }
  };
}